/**
 * Paddle.js (Billing v2) — the checkout of our Merchant of Record. The script is
 * loaded on demand (only the pricing page needs it), initialized once with the
 * public client-side token, and then opens checkouts for transactions the API
 * created. Card data never touches our pages: the form is Paddle's iframe.
 *
 * The checkout is INLINE — Paddle's frame is embedded in our own CheckoutModal,
 * next to our order summary — because only the inline checkout takes the
 * "Branded inline checkout" styling set in the Paddle dashboard (fonts, colours,
 * buttons; values in docs/PADDLE_BRANDING.md). The overlay can only be themed
 * light/dark.
 *
 * Payment links: Paddle's "default payment link" (<site>/pricing) and emailed
 * links land on `/pricing?_ptxn=<transaction id>`. Paddle.js would open those by
 * itself on Initialize — as an overlay, or inline only if our frame container
 * already existed at that instant. Rather than race it, the pricing page takes
 * the id out of the URL before Paddle.js loads (`clearPaymentLinkFromUrl`)
 * and opens it in the same modal as a normal purchase.
 */

const PADDLE_JS_URL = "https://cdn.paddle.com/paddle/v2/paddle.js";

/** Money in a checkout event: numbers in the currency's MAJOR units (10.5 = $10.50). */
export interface PaddleCheckoutTotals {
  subtotal?: number;
  tax?: number;
  total?: number;
  discount?: number;
}

export interface PaddleEvent {
  name?: string;
  data?: {
    customer?: { email?: string | null; address?: { country_code?: string | null } | null } | null;
    currency_code?: string;
    totals?: PaddleCheckoutTotals;
    items?: { product?: { name?: string | null } | null }[];
  };
}

/**
 * Billing countries Lorvok does not sell to — the Terms say the Service is not
 * offered there (the UK for lack of a UK GDPR representative). Paddle reports the
 * buyer's country as they fill it in, before any payment details, so the
 * checkout is closed at that point.
 */
export const BLOCKED_BILLING_COUNTRIES: readonly string[] = ["GB", "RU"];

/** The blocked billing country a checkout event carries, or null. */
export function blockedBillingCountry(event: PaddleEvent): string | null {
  if (event.name !== "checkout.customer.created" && event.name !== "checkout.customer.updated") return null;
  const country = event.data?.customer?.address?.country_code?.toUpperCase();
  return country && BLOCKED_BILLING_COUNTRIES.includes(country) ? country : null;
}

/** Paddle.js `Checkout.open` settings we use (a subset of Paddle's). */
export interface PaddleCheckoutSettings {
  displayMode?: "overlay" | "inline";
  variant?: "one-page" | "multi-page";
  theme?: "dark" | "light";
  locale?: string;
  successUrl?: string;
  /** Inline only: the CLASS NAME (not a selector) of the element the frame goes into. */
  frameTarget?: string;
  frameInitialHeight?: number;
  frameStyle?: string;
  showAddDiscounts?: boolean;
  /** Whether the "Add VAT / tax number" link (business reverse-charge) is shown. */
  showAddTaxId?: boolean;
}

interface PaddleGlobal {
  Environment: { set(environment: string): void };
  Initialize(options: { token: string; eventCallback?: (event: PaddleEvent) => void }): void;
  Checkout: {
    close(): void;
    open(options: { transactionId: string; customer?: { email: string }; settings?: PaddleCheckoutSettings }): void;
  };
}

/** Class of the element CheckoutModal renders for Paddle's inline frame. */
export const CHECKOUT_FRAME_CLASS = "lorvok-checkout-frame";

declare global {
  interface Window {
    Paddle?: PaddleGlobal;
  }
}

/** Public client-side token (VITE_PADDLE_CLIENT_TOKEN); null when checkout is not configured. */
export const paddleClientToken: string | null =
  (import.meta.env.VITE_PADDLE_CLIENT_TOKEN as string | undefined)?.trim() || null;

const listeners = new Set<(event: PaddleEvent) => void>();
let ready: Promise<PaddleGlobal> | null = null;

function injectScript(): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PADDLE_JS_URL;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load the payment form. Check your connection and try again."));
    document.head.appendChild(script);
  });
}

/**
 * Loads and initializes Paddle.js once for the given environment. Rejects when no
 * client token is configured or the script cannot load (the caller shows it).
 */
export function loadPaddle(environment: "sandbox" | "production"): Promise<PaddleGlobal> {
  if (ready) return ready;
  const token = paddleClientToken;
  if (!token) return Promise.reject(new Error("Payments are not configured."));
  ready = injectScript()
    .then(() => {
      const paddle = window.Paddle;
      if (!paddle) throw new Error("Could not load the payment form.");
      if (environment === "sandbox") paddle.Environment.set("sandbox");
      paddle.Initialize({
        token,
        eventCallback: (event) => {
          // Close before the buyer can pay; listeners still get the event so the
          // page can say why. Also covers checkouts opened by a ?_ptxn= link.
          if (blockedBillingCountry(event)) paddle.Checkout.close();
          listeners.forEach((listener) => listener(event));
        }
      });
      return paddle;
    })
    .catch((error: unknown) => {
      ready = null;
      throw error;
    });
  return ready;
}

/** Subscribes to Paddle.js checkout events (e.g. "checkout.completed"). */
export function onPaddleEvent(listener: (event: PaddleEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Opens Paddle's inline checkout for a transaction the API created, inside the
 * `.lorvok-checkout-frame` element — which must already be in the document
 * (CheckoutModal calls this from an effect). One page (no "next" steps), no
 * discount-code field (we sell no codes); the frame is transparent so our modal's
 * surface shows through, and it grows with its content on its own.
 * `signal`: the modal closed while Paddle.js was still loading — open nothing.
 */
export async function openPaddleCheckout(
  environment: "sandbox" | "production",
  transactionId: string,
  options: { email?: string | null; locale?: string; signal?: AbortSignal } = {}
): Promise<void> {
  const paddle = await loadPaddle(environment);
  if (options.signal?.aborted) return;
  paddle.Checkout.open({
    transactionId,
    ...(options.email ? { customer: { email: options.email } } : {}),
    settings: {
      displayMode: "inline",
      variant: "one-page",
      theme: "dark",
      frameTarget: CHECKOUT_FRAME_CLASS,
      frameInitialHeight: 450,
      frameStyle: "width: 100%; min-width: 312px; background-color: transparent; border: none;",
      showAddDiscounts: false,
      // Lorvok sells to consumers: no business tax-number link in the form.
      showAddTaxId: false,
      ...(options.locale ? { locale: options.locale } : {})
    }
  });
}

/** Closes the open checkout (removes the inline frame). A no-op before Paddle.js has loaded. */
export function closePaddleCheckout(): void {
  window.Paddle?.Checkout.close();
}

/** A payment link's transaction id in the address bar (`?_ptxn=`), or null. Pure. */
export function paymentLinkTransaction(): string | null {
  if (typeof window === "undefined") return null;
  return new URL(window.location.href).searchParams.get("_ptxn")?.trim() || null;
}

/**
 * Removes `_ptxn` from the address bar, keeping the rest of the URL. Must run
 * BEFORE `loadPaddle`: with the parameter gone Paddle.js does not auto-open its
 * overlay, and the pricing page opens the transaction in CheckoutModal instead.
 */
export function clearPaymentLinkFromUrl(): void {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("_ptxn")) return;
  url.searchParams.delete("_ptxn");
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
}
